import {
  type AppointmentCreateInput,
  AppointmentDayCountsSchema,
  AppointmentListResponseSchema,
  type AppointmentRescheduleInput,
  AppointmentSchema,
  AvailabilitySchema,
  type BookingSettingsInput,
  type ClinicSection,
  type SectionKind,
  BookingSettingsSchema,
  PractitionerListResponseSchema,
  type PractitionerSettingsInput,
  PractitionerSettingsSchema,
  type ResourceInput,
  ResourceListResponseSchema,
  ResourceSchema,
  type ServiceInput,
  ServiceListResponseSchema,
  ServiceSchema,
  UpcomingAppointmentsResponseSchema,
  type WalkInInput,
} from '@clinic/contracts';
import { createInfiniteQuery, createQuery, useQueryClient } from '@tanstack/svelte-query';
import { createMutation } from '../../../shared/api/mutation';
import { apiRequest } from '../../../shared/api/client';
import { type MaybeGetter, read } from '../../../shared/lib/maybe';
import { keys } from '../../../shared/api/keys';
import { useOrg } from '../../../shared/auth/org.svelte';

export function useServices(orgId: string, includeArchived: MaybeGetter<boolean> = false) {
  return createQuery(() => ({
    queryKey: [...keys.services(orgId), { includeArchived: read(includeArchived) }],
    queryFn: ({ signal }) =>
      apiRequest(`/v1/clinic/services?includeArchived=${read(includeArchived)}`, {
        orgId,
        schema: ServiceListResponseSchema,
        signal,
      }),
    select: (d) => d.items,
  }));
}

export function useResources(orgId: string, includeArchived: MaybeGetter<boolean> = false) {
  return createQuery(() => ({
    queryKey: [...keys.resources(orgId), { includeArchived: read(includeArchived) }],
    queryFn: ({ signal }) =>
      apiRequest(`/v1/clinic/resources?includeArchived=${read(includeArchived)}`, {
        orgId,
        schema: ResourceListResponseSchema,
        signal,
      }),
    select: (d) => d.items,
  }));
}

export function usePractitioners(orgId: string) {
  return createQuery(() => ({
    queryKey: keys.practitioners(orgId),
    queryFn: ({ signal }) =>
      apiRequest('/v1/clinic/practitioners', { orgId, schema: PractitionerListResponseSchema, signal }),
    select: (d) => d.items,
  }));
}

/** The signed-in doctor's own booking preferences (ADR-034). */
export function useMyPractitionerSettings(orgId: string) {
  return createQuery(() => ({
    queryKey: [...keys.practitioners(orgId), 'me'],
    queryFn: ({ signal }) =>
      apiRequest('/v1/clinic/practitioner-settings/me', { orgId, schema: PractitionerSettingsSchema, signal }),
  }));
}

export function useSaveMyPractitionerSettings(orgId: string) {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (body: PractitionerSettingsInput) =>
      apiRequest('/v1/clinic/practitioner-settings/me', {
        method: 'PUT',
        orgId,
        body,
        schema: PractitionerSettingsSchema,
      }),
    // The practitioner list carries `allowsOverlap` for the booking form.
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.practitioners(orgId) }),
  }));
}

/** The clinic's booking settings (ADR-035). */
export function useBookingSettings(orgId: string) {
  return createQuery(() => ({
    queryKey: keys.bookingSettings(orgId),
    queryFn: ({ signal }) => apiRequest('/v1/clinic/booking-settings', { orgId, schema: BookingSettingsSchema, signal }),
    staleTime: 60_000,
  }));
}

/** Whether this clinic checks patients in before the visit; the server's rule until it has answered. */
/** Whether the clinic checks patients in (ADR-035); read `.value`, it follows the settings. */
export function useCheckInRequired(orgId: string): { readonly value: boolean } {
  const settings = useBookingSettings(orgId);
  return {
    get value() {
      return settings.data?.checkInRequired ?? false;
    },
  };
}

export function useSaveBookingSettings(orgId: string) {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (body: BookingSettingsInput) =>
      apiRequest('/v1/clinic/booking-settings', { method: 'PUT', orgId, body, schema: BookingSettingsSchema }),
    onSuccess: (data) => qc.setQueryData(keys.bookingSettings(orgId), data),
  }));
}

function rangeQuery(orgId: string, from: Date, to: Date) {
  return {
    queryKey: [...keys.appointments(orgId), 'range', from.toISOString(), to.toISOString()],
    queryFn: ({ signal }: { signal: AbortSignal }) => {
      const params = new URLSearchParams({ from: from.toISOString(), to: to.toISOString() });
      return apiRequest(`/v1/clinic/appointments?${params}`, { orgId, schema: AppointmentListResponseSchema, signal });
    },
  };
}

export function useAppointmentsRange(orgId: string, from: MaybeGetter<Date>, to: MaybeGetter<Date>) {
  return createQuery(() => ({ ...rangeQuery(orgId, read(from), read(to)), select: (d) => d.items }));
}

/** Time taken in sections the caller does not see (ADR-036); the same request as the range. */
export function useBusyRange(orgId: string, from: MaybeGetter<Date>, to: MaybeGetter<Date>) {
  return createQuery(() => ({ ...rangeQuery(orgId, read(from), read(to)), select: (d) => d.busy }));
}

/**
 * The sections worth a filter for this kind of work: the caller's own, and of those beyond the
 * clinic part only the ones the clinic has services in (no «لیزر» chip in a clinic without laser).
 */
export function useSectionsInUse(kind: MaybeGetter<SectionKind>): { readonly value: ClinicSection[] } {
  const org = useOrg();
  const services = useServices(org.orgId, true);
  return {
    get value() {
      const mine = org.sections[read(kind)];
      if (mine.length < 2) return mine;
      const used = new Set(services.data?.map((s) => s.section) ?? []);
      return mine.filter((s) => s === 'general' || used.has(s));
    },
  };
}

export function usePatientAppointments(orgId: string, patientId: MaybeGetter<string>) {
  return createInfiniteQuery(() => ({
    queryKey: [...keys.appointments(orgId), 'patient', read(patientId)],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) => {
      const params = new URLSearchParams({ patientId: read(patientId), limit: '20' });
      if (pageParam) params.set('cursor', pageParam);
      return apiRequest(`/v1/clinic/appointments?${params}`, { orgId, schema: AppointmentListResponseSchema, signal });
    },
    getNextPageParam: (last) => last.nextCursor,
  }));
}

/** The coming appointments of some patients (a page of the patient list, or one in a form), soonest first. */
export function useUpcomingAppointments(orgId: string, patientIds: MaybeGetter<readonly string[]>, enabled: MaybeGetter<boolean> = true) {
  return createQuery(() => ({
    queryKey: [...keys.appointments(orgId), 'upcoming', read(patientIds)],
    queryFn: ({ signal }) =>
      apiRequest(`/v1/clinic/appointments/upcoming?patientIds=${read(patientIds).join(',')}`, {
        orgId,
        schema: UpcomingAppointmentsResponseSchema,
        signal,
      }),
    select: (d) => d.items,
    enabled: read(enabled) && read(patientIds).length > 0,
  }));
}

/**
 * Patients booked on each day of the month a date picker shows, as `{ 'YYYY-MM-DD': n }`. Under the
 * appointments key, so a booking or a live update refreshes it. Nothing is asked before `range` is set.
 */
export function useAppointmentDayCounts(
  orgId: string,
  q: MaybeGetter<{ from: string; to: string; practitionerId?: string | null; section?: ClinicSection | null } | null>,
) {
  return createQuery(() => ({
    queryKey: [...keys.appointments(orgId), 'day-counts', read(q)],
    queryFn: ({ signal }) => {
      const { from, to, practitionerId, section } = read(q)!;
      const params = new URLSearchParams({ from, to });
      if (practitionerId) params.set('practitionerId', practitionerId);
      if (section) params.set('section', section);
      return apiRequest(`/v1/clinic/appointments/day-counts?${params}`, { orgId, schema: AppointmentDayCountsSchema, signal });
    },
    enabled: read(q) !== null,
    select: (d) => Object.fromEntries(d.items.map((i) => [i.date, i.patients])),
  }));
}

/**
 * A booking aid (BR-62): the time taken on a day and the first free start. Under the appointments
 * key, so a booking anywhere (or a live update) refreshes it; the server still decides.
 */
export function useAvailability(
  orgId: string,
  q: MaybeGetter<{ practitionerId: string; serviceId: string; date: string; durationMinutes: number | null; resourceIds: readonly string[] }>,
  enabled: MaybeGetter<boolean> = true,
) {
  return createQuery(() => ({
    queryKey: [...keys.appointments(orgId), 'availability', read(q)],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({
        practitionerMembershipId: read(q).practitionerId,
        serviceId: read(q).serviceId,
        date: read(q).date,
      });
      if (read(q).durationMinutes !== null) params.set('durationMinutes', String(read(q).durationMinutes));
      if (read(q).resourceIds.length > 0) params.set('resourceIds', read(q).resourceIds.join(','));
      return apiRequest(`/v1/clinic/appointments/availability?${params}`, { orgId, schema: AvailabilitySchema, signal });
    },
    enabled: read(enabled) && Boolean(read(q).practitionerId && read(q).serviceId && read(q).date),
  }));
}

function useAppointmentMutation<V, R>(orgId: string, fn: (vars: V) => Promise<R>) {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: fn,
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: keys.appointments(orgId) }),
        qc.invalidateQueries({ queryKey: keys.smsQueue(orgId) }),
        qc.invalidateQueries({ queryKey: keys.visits(orgId) }),
      ]),
  }));
}

export function useCreateAppointment(orgId: string) {
  return useAppointmentMutation(orgId, (body: AppointmentCreateInput) =>
    apiRequest('/v1/clinic/appointments', { method: 'POST', body, orgId, schema: AppointmentSchema }),
  );
}

export function useRescheduleAppointment(orgId: string) {
  return useAppointmentMutation(orgId, ({ id, body }: { id: string; body: AppointmentRescheduleInput }) =>
    apiRequest(`/v1/clinic/appointments/${id}/reschedule`, { method: 'POST', body, orgId, schema: AppointmentSchema }),
  );
}

/** A patient without an appointment: the server books the doctor's first free time today (ADR-035). */
export function useWalkIn(orgId: string) {
  return useAppointmentMutation(orgId, (body: WalkInInput) =>
    apiRequest('/v1/clinic/appointments/walk-in', { method: 'POST', body, orgId, schema: AppointmentSchema }),
  );
}

export type AppointmentCommand = 'confirm' | 'check-in' | 'cancel' | 'no-show';

export function useAppointmentCommand(orgId: string) {
  return useAppointmentMutation(orgId, ({ id, command, reason }: { id: string; command: AppointmentCommand; reason?: string }) =>
    apiRequest(`/v1/clinic/appointments/${id}/${command}`, {
      method: 'POST',
      body: command === 'cancel' ? { reason: reason || null } : {},
      orgId,
      schema: AppointmentSchema,
    }),
  );
}

// ---- catalog (settings) ----

function useCatalogMutation<V, R>(orgId: string, key: MaybeGetter<'services' | 'resources'>, fn: (vars: V) => Promise<R>) {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: fn,
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: keys[read(key)](orgId) }),
        qc.invalidateQueries({ queryKey: keys.appointments(orgId) }),
      ]),
  }));
}

export function useSaveService(orgId: string) {
  return useCatalogMutation(orgId, 'services', ({ id, body }: { id: string | null; body: ServiceInput }) =>
    apiRequest(id ? `/v1/clinic/services/${id}` : '/v1/clinic/services', {
      method: id ? 'PUT' : 'POST',
      body,
      orgId,
      schema: ServiceSchema,
    }),
  );
}

export function useSetServiceArchived(orgId: string) {
  return useCatalogMutation(orgId, 'services', ({ id, archived }: { id: string; archived: boolean }) =>
    apiRequest(`/v1/clinic/services/${id}/${archived ? 'archive' : 'restore'}`, {
      method: 'POST',
      orgId,
      schema: ServiceSchema,
    }),
  );
}

export function useSaveResource(orgId: string) {
  return useCatalogMutation(orgId, 'resources', ({ id, body }: { id: string | null; body: ResourceInput }) =>
    apiRequest(id ? `/v1/clinic/resources/${id}` : '/v1/clinic/resources', {
      method: id ? 'PUT' : 'POST',
      body,
      orgId,
      schema: ResourceSchema,
    }),
  );
}

export function useSetResourceArchived(orgId: string) {
  return useCatalogMutation(orgId, 'resources', ({ id, archived }: { id: string; archived: boolean }) =>
    apiRequest(`/v1/clinic/resources/${id}/${archived ? 'archive' : 'restore'}`, {
      method: 'POST',
      orgId,
      schema: ResourceSchema,
    }),
  );
}
